var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { clone, cloneDeep, extend, filter, forEach, get, has, head, isArray, isEmpty, isEqual, isNumber, isObject, isString, isUndefined, map, once, set, throttle, unset } from 'lodash';
import { Stratus } from '@stratusjs/runtime/stratus';
import { ErrorBase } from '@stratusjs/core/errors/errorBase';
import { getAnchorParams, getUrlParams, isJSON, patch, serializeUrlParams, setUrlParams, strcmp, ucfirst } from '@stratusjs/core/misc';
import { ModelBase } from '@stratusjs/core/datastore/modelBase';
import { cookie } from '@stratusjs/core/environment';
import { XHR } from '@stratusjs/core/datastore/xhr';
import { getInjector } from '../injector';
import { Collection } from './collection';
import Toastify from 'toastify-js';
let injector = getInjector();
let $rootScope;
const genericValidationMessages = [
    'There were validation errors.'
];
const transientWriteFailureCode = 'API_TRANSIENT_WRITE_FAILURE';
const retryableWriteMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
const transientWriteRetryBackoff = [250, 750, 1500];
const transientWriteRetryMaxAttempts = 3;
function normalizeStatusList(source) {
    const status = get(source, 'meta.status') ||
        get(source, 'payload.meta.status') ||
        get(source, 'payload.status') ||
        get(source, 'status') ||
        source;
    if (isArray(status)) {
        return status.filter((entry) => isObject(entry));
    }
    if (isObject(status) &&
        (isString(get(status, 'code')) ||
            isString(get(status, 'message')) ||
            isString(get(status, 'type')))) {
        return [status];
    }
    return [];
}
function parseXHRPayload(error) {
    if (error instanceof ErrorBase) {
        return get(error, 'payload') || error;
    }
    if (isObject(error) && !isUndefined(get(error, 'response'))) {
        return get(error, 'response');
    }
    const responseText = get(error, 'responseText');
    return isString(responseText) && isJSON(responseText) ? JSON.parse(responseText) : null;
}
function isGenericValidationMessage(message) {
    return genericValidationMessages.indexOf(message.trim()) !== -1;
}
function hasStatusCode(source, code) {
    return normalizeStatusList(source).some((status) => status.code === code);
}
function isRetryableTransientWritePayload(action, payload) {
    const method = (action || 'GET').toUpperCase();
    if (retryableWriteMethods.indexOf(method) === -1) {
        return false;
    }
    if (!payload) {
        return false;
    }
    return !!(get(payload, 'code') === transientWriteFailureCode ||
        hasStatusCode(payload, transientWriteFailureCode));
}
function isRetryableTransientWrite(action, error) {
    const payload = parseXHRPayload(error);
    if (!payload) {
        return false;
    }
    return isRetryableTransientWritePayload(action, payload);
}
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
function getPreferredStatusMessage(source) {
    const statuses = normalizeStatusList(source);
    if (!statuses.length) {
        return null;
    }
    const messages = statuses
        .map((status) => isString(status.message) ? status.message.trim() : '')
        .filter((message) => !!message);
    if (!messages.length) {
        return null;
    }
    const specificMessage = messages.find((message) => !isGenericValidationMessage(message));
    return specificMessage || messages[0];
}
function hasNotableStatus(source) {
    return normalizeStatusList(source).some((status) => {
        const type = isString(status.type) ? status.type.toLowerCase() : '';
        return type === 'warning' || type === 'error' || status.code === 'VALIDATION';
    });
}
function hasUsablePayload(payload) {
    return (isArray(payload) && !!payload.length) || (isObject(payload) && !isArray(payload));
}
function isSoftStatusFailure(meta, payload) {
    return !!(get(meta, 'success') === false &&
        hasUsablePayload(payload) &&
        getPreferredStatusMessage(meta) &&
        hasNotableStatus(meta));
}
const serviceVerify = () => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, _reject) => __awaiter(void 0, void 0, void 0, function* () {
        if ($rootScope) {
            resolve(true);
            return;
        }
        if (!injector) {
            injector = getInjector();
        }
        if (injector) {
            $rootScope = injector.get('$rootScope');
        }
        if ($rootScope) {
            resolve(true);
            return;
        }
        setTimeout(() => {
            if (cookie('env')) {
                console.log('wait for $rootScope service:', {
                    $rootScope
                });
            }
            serviceVerify().then(resolve);
        }, 250);
    }));
});
export const ModelOptionKeys = ["autoSave", "autoSaveInterval", "autoSaveHalt", "collection", "completed", "manifest", "serviceId", "stagger", "target", "targetSuffix", "toast", "type", "urlRoot", "urlSync", "watch", "withCredentials", "payload", "convoy", "headers", "ignoreKeys", "received"];
export class Model extends ModelBase {
    constructor(options = {}, attributes) {
        super(attributes);
        this.name = 'Model';
        this.target = null;
        this.type = null;
        this.manifest = false;
        this.stagger = false;
        this.toast = true;
        this.identifier = null;
        this.urlRoot = '/Api';
        this.targetSuffix = null;
        this.serviceId = null;
        this.header = new ModelBase();
        this.meta = new ModelBase();
        this.route = new ModelBase();
        this.collection = null;
        this.withCredentials = false;
        this.headers = {};
        this.pending = false;
        this.error = false;
        this.completed = false;
        this.saving = false;
        this.changedExternal = false;
        this.watch = true;
        this.status = null;
        this.autoSave = false;
        this.autoSaveInterval = 4000;
        this.autoSaveHalt = true;
        this.autoSaveTimeout = null;
        this.urlSync = false;
        this.bracket = {
            match: /\[[\d+]]/,
            search: /\[([\d+])]/g,
            attr: /(^[^[]+)/
        };
        this.throttle = throttle(this.fetch, 1000);
        this.initialize = null;
        options = typeof options !== 'object' ? {} : options;
        options.received = options.received || false;
        extend(this, this.sanitizeOptions(options));
        if (options.convoy) {
            const convoy = isJSON(options.convoy) ? JSON.parse(options.convoy) : options.convoy;
            if (isObject(convoy)) {
                this.meta.set(convoy.meta || {});
                const payload = convoy.payload;
                if (isObject(payload)) {
                    extend(this.data, payload);
                    this.completed = true;
                    options.received = true;
                }
                else {
                    console.error('malformed payload:', payload);
                }
            }
            else {
                console.error('malformed convoy:', convoy);
            }
        }
        if (options.payload) {
            const payload = isJSON(options.payload) ? JSON.parse(options.payload) : options.payload;
            if (isObject(payload)) {
                extend(this.data, payload);
                this.completed = true;
                options.received = true;
            }
            else {
                console.error('malformed payload:', payload);
            }
        }
        this.header = new ModelBase();
        this.meta = new ModelBase();
        this.route = new ModelBase();
        if (!isEmpty(this.collection)) {
            if (this.collection.target) {
                this.target = this.collection.target;
            }
            if (this.collection.meta.has('api')) {
                this.meta.set('api', this.collection.meta.get('api'));
            }
        }
        this.recv = options.received ? cloneDeep(this.data) : {};
        this.sent = {};
        this.ignoreKeys = options.ignoreKeys || ['$$hashKey'];
        if (this.target) {
            this.urlRoot += '/' + ucfirst(this.target);
        }
        const that = this;
        this.initialize = once(this.initialize || function defaultInitializer() {
            if (that.completed && (that.watch || that.autoSave)) {
                that.watcher().then();
            }
            if (that.manifest && !that.getIdentifier()) {
                that.sync('POST', that.meta.has('api') ? {
                    meta: that.meta.get('api'),
                    payload: {}
                } : {}).catch((error) => __awaiter(this, void 0, void 0, function* () {
                    console.error('MANIFEST:', error);
                    if (!that.toast) {
                        return;
                    }
                    const errorMessage = that.errorMessage(error);
                    const formatMessage = errorMessage ? `: ${errorMessage}` : '.';
                    Toastify({
                        text: `Unable to Manifest ${that.target}${formatMessage}`,
                        duration: 12000,
                        close: true,
                        stopOnFocus: true,
                        style: {
                            background: '#E14D45',
                        }
                    }).showToast();
                    that.errorMessage(error);
                }));
            }
        });
        if (!this.stagger) {
            this.initialize();
        }
    }
    resetXHRFlags() {
        this.pending = false;
        this.saving = false;
    }
    sanitizeOptions(options) {
        const sanitizedOptions = {};
        forEach(ModelOptionKeys, (key) => {
            const data = get(options, key);
            if (isUndefined(data)) {
                return;
            }
            set(sanitizedOptions, key, data);
        });
        return sanitizedOptions;
    }
    getReadOnlyFields() {
        const fields = this.meta.get('readOnlyFields');
        return isArray(fields) ? fields.filter((field) => isString(field) && !!field) : [];
    }
    deletePath(obj, path) {
        if (!obj || !isString(path)) {
            return;
        }
        unset(obj, path);
    }
    pruneEmptyBranches(obj) {
        if (!isObject(obj) || isArray(obj)) {
            return isEmpty(obj);
        }
        const target = obj;
        for (const key of Object.keys(target)) {
            const value = target[key];
            if (isObject(value) && !isArray(value)) {
                const emptyChild = this.pruneEmptyBranches(value);
                if (emptyChild) {
                    delete target[key];
                }
                continue;
            }
            if (isUndefined(value)) {
                delete target[key];
            }
        }
        return isEmpty(target);
    }
    sanitizeReadOnlyPatchPayload(payload) {
        if (!isObject(payload)) {
            return payload;
        }
        const sanitized = cloneDeep(payload);
        const readOnlyFields = this.getReadOnlyFields();
        if (!readOnlyFields.length) {
            return sanitized;
        }
        forEach(readOnlyFields, (path) => {
            this.deletePath(sanitized, path);
        });
        this.pruneEmptyBranches(sanitized);
        return sanitized;
    }
    getSavablePatch() {
        return this.sanitizeReadOnlyPatchPayload(cloneDeep(this.toPatch()));
    }
    watcher() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.watching) {
                return true;
            }
            this.watching = true;
            if (!$rootScope) {
                yield serviceVerify();
            }
            $rootScope.$watch(() => this.data, (_newData, _priorData) => this.handleChanges(), true);
        });
    }
    handleChanges(changeSet) {
        const isUserChangeSet = isUndefined(changeSet);
        if (isUserChangeSet) {
            changeSet = super.handleChanges();
            changeSet = this.sanitizeReadOnlyPatchPayload(changeSet);
            if (!isEmpty(this.patch)) {
                this.patch = this.sanitizeReadOnlyPatchPayload(this.patch);
            }
            this.changed = !isEmpty(this.patch);
        }
        if (!changeSet || isEmpty(changeSet)) {
            return changeSet;
        }
        if (this.error && !this.completed && this.getIdentifier()) {
            const action = isUserChangeSet ? 'save' : 'sync url for';
            console.warn(`Blocked attempt to ${action} a persisted model that has not been fetched successfully.`);
            return;
        }
        if (!isUserChangeSet) {
            if (cookie('env')) {
                console.info('Attempting URL Sync for non-User ChangeSet:', changeSet);
            }
        }
        if (this.urlSync) {
            if (get(changeSet, 'id')) {
                const newUrl = setUrlParams({
                    id: get(changeSet, 'id') || this.getIdentifier()
                });
                if (newUrl !== document.location.href) {
                    window.location.replace(newUrl);
                }
            }
            const version = getAnchorParams('version');
            const versionId = !isEmpty(version) ? parseInt(version, 10) : 0;
            if (versionId && versionId !== get(changeSet, 'version.id')) {
                if (cookie('env')) {
                    console.warn('replacing version:', versionId);
                }
            }
        }
        if (!isUserChangeSet) {
            return;
        }
        this.saveIdle();
        this.throttleTrigger('change', this);
        if (this.collection) {
            this.collection.throttleTrigger('change', this);
        }
        return changeSet;
    }
    getIdentifier() {
        return (this.identifier = this.get('id') || this.route.get('identifier') || this.identifier);
    }
    getType() {
        return (this.type = this.type || this.target || 'orphan');
    }
    getHash() {
        return this.getType() + (isNumber(this.getIdentifier()) ? this.getIdentifier().toString() : this.getIdentifier());
    }
    isNew() {
        return !this.getIdentifier();
    }
    url() {
        let url = this.getIdentifier() ? `${this.urlRoot}/${this.getIdentifier()}` : `${this.urlRoot}${this.targetSuffix || ''}`;
        if (getUrlParams('version')) {
            url += url.includes('?') ? '&' : '?';
            url += 'options[version]=' + getUrlParams('version');
        }
        return url;
    }
    serialize(obj, chain) { return serializeUrlParams(obj, chain); }
    sync(action, data, options) {
        this.pending = true;
        this.trigger('change', this);
        if (this.collection) {
            this.collection.pending = true;
            this.collection.throttleTrigger('change');
        }
        this.sent = cloneDeep(this.data);
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            let successfulResponseMeta = null;
            action = action || 'GET';
            options = options || {};
            const request = {
                method: action,
                url: this.url(),
                headers: clone(this.headers),
                withCredentials: this.withCredentials,
            };
            if (!isUndefined(data)) {
                if (['GET', 'DELETE'].includes(action)) {
                    if (isObject(data) && Object.keys(data).length) {
                        request.url += request.url.includes('?') ? '&' : '?';
                        request.url += this.serialize(data);
                    }
                }
                else {
                    request.headers['Content-Type'] = 'application/json';
                    request.data = data;
                }
            }
            if (cookie('env')) {
                console.log('Prototype:', request);
            }
            if (Object.prototype.hasOwnProperty.call(options, 'headers') && typeof options.headers === 'object') {
                Object.keys(options.headers).forEach((headerKey) => {
                    request.headers[headerKey] = options.headers[headerKey];
                });
            }
            const sendRequest = (attempt = 1) => __awaiter(this, void 0, void 0, function* () {
                this.xhr = new XHR(request);
                try {
                    const response = yield this.xhr.send();
                    if (attempt < transientWriteRetryMaxAttempts &&
                        isRetryableTransientWritePayload(request.method, response)) {
                        const nextAttempt = attempt + 1;
                        console.warn(`XHR: ${request.method} ${request.url} returned ${transientWriteFailureCode}; retrying ${nextAttempt} of ${transientWriteRetryMaxAttempts}.`, response);
                        if (this.toast) {
                            Toastify({
                                text: `The server was busy saving. Retrying ${nextAttempt} of ${transientWriteRetryMaxAttempts}...`,
                                duration: 4000,
                                close: true,
                                stopOnFocus: true,
                                style: {
                                    background: '#D9902F',
                                }
                            }).showToast();
                        }
                        yield delay(transientWriteRetryBackoff[attempt - 1] || transientWriteRetryBackoff[transientWriteRetryBackoff.length - 1]);
                        return sendRequest(nextAttempt);
                    }
                    return response;
                }
                catch (error) {
                    if (attempt < transientWriteRetryMaxAttempts &&
                        isRetryableTransientWrite(request.method, error)) {
                        const nextAttempt = attempt + 1;
                        console.warn(`XHR: ${request.method} ${request.url} returned ${transientWriteFailureCode}; retrying ${nextAttempt} of ${transientWriteRetryMaxAttempts}.`, error);
                        if (this.toast) {
                            Toastify({
                                text: `The server was busy saving. Retrying ${nextAttempt} of ${transientWriteRetryMaxAttempts}...`,
                                duration: 4000,
                                close: true,
                                stopOnFocus: true,
                                style: {
                                    background: '#D9902F',
                                }
                            }).showToast();
                        }
                        yield delay(transientWriteRetryBackoff[attempt - 1] || transientWriteRetryBackoff[transientWriteRetryBackoff.length - 1]);
                        return sendRequest(nextAttempt);
                    }
                    throw error;
                }
            });
            sendRequest().then((response) => {
                this.status = this.xhr.status;
                if (this.watch || this.autoSave) {
                    this.watcher();
                }
                const propagateError = () => {
                    this.error = true;
                    this.resetXHRFlags();
                    if (this.collection) {
                        this.collection.pending = false;
                    }
                    this.trigger('error', this);
                    this.trigger('complete', this);
                    if (this.collection instanceof Collection) {
                        this.collection.throttleTrigger('change');
                    }
                };
                if (!isObject(response) && !isArray(response)) {
                    const error = new ErrorBase({
                        payload: response,
                        message: `Invalid Payload: ${request.method} ${request.url}`
                    }, {});
                    propagateError();
                    reject(error);
                    return;
                }
                this.header.set(this.xhr.getAllResponseHeaders() || {});
                successfulResponseMeta = response.meta || null;
                const responseMeta = successfulResponseMeta || {};
                this.meta.set(responseMeta);
                this.route.set(response.route || {});
                const payload = response.payload || response;
                this.error = false;
                if ((this.meta.has('success') && !this.meta.get('success') && !isSoftStatusFailure(responseMeta, payload))) {
                    this.error = true;
                }
                else if (isArray(payload) && payload.length) {
                    this.recv = head(payload);
                }
                else if (isObject(payload) && !isArray(payload)) {
                    this.recv = payload;
                }
                else {
                    if (!this.meta.has('status') && !this.meta.has('success')) {
                        this.error = true;
                    }
                    console.warn(`Invalid Payload: ${request.method} ${request.url}`);
                }
                if (this.error) {
                    const error = new ErrorBase({
                        payload: response,
                        message: `Invalid Payload: ${request.method} ${request.url}`
                    }, {});
                    propagateError();
                    reject(error);
                    return;
                }
                const incomingChangeSet = this.completed ? cloneDeep(patch(this.recv, this.sent)) : {};
                if (!isEmpty(incomingChangeSet)) {
                    if (cookie('env')) {
                        console.log('Incoming ChangeSet detected:', cookie('debug_change_set')
                            ? JSON.stringify(incomingChangeSet)
                            : incomingChangeSet);
                    }
                    this.handleChanges(incomingChangeSet);
                }
                const intermediateData = cloneDeep(this.recv);
                const intermediateChangeSet = cloneDeep(patch(this.data, this.sent));
                if (!isEmpty(intermediateChangeSet)) {
                    if (cookie('env')) {
                        console.log('Intermediate ChangeSet detected:', cookie('debug_change_set')
                            ? JSON.stringify(intermediateChangeSet)
                            : intermediateChangeSet);
                    }
                    forEach(intermediateChangeSet, (element, key) => {
                        set(intermediateData, key, element);
                    });
                }
                this.data = cloneDeep(intermediateData);
                this.changed = false;
                this.changedExternal = false;
                this.saving = false;
                this.handleChanges();
                this.patch = {};
                this.resetXHRFlags();
                this.completed = true;
                if (this.collection) {
                    this.collection.pending = false;
                }
                this.meta.clearTemp();
                const statusMessage = getPreferredStatusMessage(response.meta || {});
                if (this.toast && statusMessage && hasNotableStatus(response.meta || {})) {
                    Toastify({
                        text: statusMessage,
                        duration: 12000,
                        close: true,
                        stopOnFocus: true,
                        style: {
                            background: '#D9902F',
                        }
                    }).showToast();
                }
                this.trigger('success', this);
                this.trigger('change', this);
                this.trigger('complete', this);
                if (this.collection instanceof Collection) {
                    this.collection.throttleTrigger('change');
                }
                resolve(this.data);
                return;
            })
                .catch((error) => {
                if (successfulResponseMeta && get(successfulResponseMeta, 'success') === true) {
                    console.warn(`XHR: ${request.method} ${request.url} completed with API success but post-save handling failed.`, error);
                    this.error = false;
                    this.resetXHRFlags();
                    this.completed = true;
                    if (this.collection) {
                        this.collection.pending = false;
                    }
                    const statusMessage = getPreferredStatusMessage(successfulResponseMeta);
                    if (this.toast && statusMessage && hasNotableStatus(successfulResponseMeta)) {
                        Toastify({
                            text: statusMessage,
                            duration: 12000,
                            close: true,
                            stopOnFocus: true,
                            style: {
                                background: '#D9902F',
                            }
                        }).showToast();
                    }
                    this.trigger('success', this);
                    this.trigger('change', this);
                    this.trigger('complete', this);
                    if (this.collection instanceof Collection) {
                        this.collection.throttleTrigger('change');
                    }
                    resolve(this.data);
                    return;
                }
                this.status = 500;
                this.error = true;
                this.resetXHRFlags();
                console.error(`XHR: ${request.method} ${request.url}`, error);
                reject(error);
                return;
            });
        }));
    }
    fetch(action, data, options) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            this.sync(action, data || this.meta.get('api'), options)
                .then(resolve)
                .catch((error) => __awaiter(this, void 0, void 0, function* () {
                this.status = 500;
                this.error = true;
                this.resetXHRFlags();
                console.error('FETCH:', error);
                if (!this.toast) {
                    reject(error);
                    return;
                }
                const errorMessage = this.errorMessage(error);
                const formatMessage = errorMessage ? `: ${errorMessage}` : '.';
                Toastify({
                    text: `Unable to Fetch ${this.target}${formatMessage}`,
                    duration: 12000,
                    close: true,
                    stopOnFocus: true,
                    style: {
                        background: '#E14D45',
                    }
                }).showToast();
                reject(error);
                return;
            }));
        }));
    }
    save(options) {
        this.saving = true;
        options = options || {};
        if (!isObject(options)) {
            console.warn('invalid options supplied:', options);
            options = {};
        }
        if (has(options, 'force') && options.force) {
            options.patch = has(options, 'patch') ? options.patch : false;
            return this.doSave(options);
        }
        if (!this.isNew() && (this.pending || !this.completed || isEmpty(this.getSavablePatch()))) {
            console.warn(`Blocked attempt to save ${isEmpty(this.toPatch()) ? 'an empty payload' : 'a duplicate XHR'} to a persisted model.`);
            return new Promise((resolve, _reject) => {
                this.saving = false;
                resolve(this.data);
            });
        }
        return this.doSave(options);
    }
    doSave(options) {
        options = options || {};
        if (!isObject(options)) {
            console.warn('invalid options supplied:', options);
            options = {};
        }
        options.patch = has(options, 'patch') ? options.patch : true;
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            this.sync(this.getIdentifier() ? 'PUT' : 'POST', this.toJSON({
                patch: options.patch
            }))
                .then(resolve)
                .catch((error) => __awaiter(this, void 0, void 0, function* () {
                this.error = true;
                this.resetXHRFlags();
                console.error('SAVE:', error);
                if (!this.toast) {
                    reject(error);
                    return;
                }
                const errorMessage = this.errorMessage(error) || getPreferredStatusMessage(this.meta.data);
                Toastify({
                    text: errorMessage || `Unable to Save ${this.target}.`,
                    duration: 12000,
                    close: true,
                    stopOnFocus: true,
                    style: {
                        background: '#E14D45',
                    }
                }).showToast();
                reject(error);
                return;
            }));
        }));
    }
    saveIdle() {
        if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
        }
        if (this.pending || !this.completed || this.isNew() || isEmpty(this.getSavablePatch())) {
            return;
        }
        if (this.autoSaveHalt && !this.autoSave) {
            return;
        }
        this.autoSaveTimeout = setTimeout(() => {
            if (!this.autoSaveHalt && !this.autoSave) {
                this.saveIdle();
                return;
            }
            this.save().then();
        }, this.autoSaveInterval);
    }
    throttleSave() {
        return new Promise((resolve, reject) => {
            const request = this.throttle();
            console.log('throttle request:', request);
            request.then((data) => {
                console.log('throttle received:', data);
                resolve(data);
            }).catch(reject);
        });
    }
    toJSON(options) {
        options = options || {};
        if (!isObject(options)) {
            options = {};
        }
        options.patch = (options.patch && !this.isNew());
        let data = super.toJSON(options);
        if (options.patch) {
            data = this.sanitizeReadOnlyPatchPayload(data);
        }
        const metaData = this.meta.get('api');
        if (metaData) {
            data = {
                meta: metaData,
                payload: data
            };
        }
        return data;
    }
    buildPath(path) {
        const acc = [];
        if (!isString(path)) {
            return acc;
        }
        let cur;
        let search;
        forEach(path.split('.'), (link) => {
            if (link.match(this.bracket.match)) {
                cur = this.bracket.attr.exec(link);
                if (cur !== null) {
                    acc.push(cur[1]);
                    cur = null;
                }
                else {
                    cur = false;
                }
                search = this.bracket.search.exec(link);
                while (search !== null) {
                    if (cur !== false) {
                        cur = parseInt(search[1], 10);
                        if (!isNaN(cur)) {
                            acc.push(cur);
                        }
                        else {
                            cur = false;
                        }
                    }
                    search = this.bracket.search.exec(link);
                }
            }
            else {
                acc.push(link);
            }
        });
        return acc;
    }
    get(attr) {
        if (typeof attr !== 'string' || !this.data || typeof this.data !== 'object') {
            return undefined;
        }
        return get(this.data, attr);
    }
    find(attr, key, value) {
        if (typeof attr === 'string') {
            attr = this.get(attr);
        }
        return !isArray(attr) ? attr : attr.find((obj) => obj[key] === value);
    }
    set(attr, value) {
        if (!attr) {
            console.warn('No attr for model.set()!');
            return this;
        }
        if (typeof attr === 'object') {
            forEach(attr, (v, k) => this.setAttribute(k, v));
            return this;
        }
        this.setAttribute(attr, value);
        return this;
    }
    setAttribute(attr, value) {
        if (typeof attr !== 'string') {
            console.warn('Malformed attr for model.setAttribute()!');
            return false;
        }
        set(this.data, attr, value);
        this.throttleTrigger('change', this);
        this.throttleTrigger(`change:${attr}`, value);
    }
    toggle(attribute, item, options) {
        if (typeof options === 'object' &&
            !isUndefined(options.multiple) &&
            isUndefined(options.strict)) {
            options.strict = true;
        }
        options = extend({
            multiple: true
        }, isObject(options) ? options : {});
        const request = attribute.split('[].');
        let target = this.get(request.length > 1 ? request[0] : attribute);
        if (isUndefined(target) ||
            (options.strict && isArray(target) !==
                options.multiple)) {
            target = options.multiple ? [] : null;
            this.set(request.length > 1 ? request[0] : attribute, target);
        }
        if (isArray(target)) {
            if (isUndefined(item)) {
                this.set(attribute, null);
            }
            else if (!this.exists(attribute, item)) {
                target.push(item);
            }
            else {
                forEach(target, (element, key) => {
                    const child = (request.length > 1 &&
                        typeof element === 'object' && request[1] in element)
                        ? element[request[1]]
                        : element;
                    const childId = (typeof child === 'object' && child.id)
                        ? child.id
                        : child;
                    const itemId = (typeof item === 'object' && item.id)
                        ? item.id
                        : item;
                    if (childId === itemId || (isString(childId) && isString(itemId) && strcmp(childId, itemId) === 0)) {
                        target.splice(key, 1);
                    }
                });
            }
        }
        else if (typeof target === 'object' || typeof target === 'number') {
            this.set(attribute, !this.exists(attribute, item) ? item : null);
        }
        else if (isUndefined(item)) {
            this.set(attribute, !target);
        }
        return this.get(attribute);
    }
    pluck(attr) {
        if (typeof attr !== 'string' || attr.indexOf('[].') === -1) {
            return this.get(attr);
        }
        const request = attr.split('[].');
        if (request.length <= 1) {
            return undefined;
        }
        attr = this.get(request[0]);
        if (!attr || !isArray(attr)) {
            return undefined;
        }
        const list = filter(map(attr, (element) => get(element, request[1])));
        return list.length ? list : undefined;
    }
    exists(attribute, item) {
        if (!item) {
            attribute = this.get(attribute);
            return typeof attribute !== 'undefined' && attribute;
        }
        if (typeof attribute === 'string' && item) {
            attribute = this.pluck(attribute);
            if (isArray(attribute)) {
                return typeof attribute.find((element) => element === item || ((typeof element === 'object' && element.id && element.id === item) || isEqual(element, item))) !== 'undefined';
            }
            return attribute === item || (typeof attribute === 'object' && attribute.id && (isEqual(attribute, item) || attribute.id === item));
        }
        return false;
    }
    destroy() {
        if (this.isNew()) {
            return new Promise((resolve, _reject) => {
                this.throttleTrigger('change');
                if (this.collection) {
                    this.collection.remove(this);
                }
                resolve(this.data);
            });
        }
        return new Promise((_resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            let deleteData = {};
            if (!isEmpty(this.meta.get('api'))) {
                deleteData = this.meta.get('api');
            }
            this.sync('DELETE', deleteData)
                .then((_data) => {
                if (this.error) {
                    reject(this.error);
                    return;
                }
                this.throttleTrigger('change');
                if (this.collection) {
                    this.collection.remove(this);
                }
                _resolve(this.data);
            })
                .catch((error) => __awaiter(this, void 0, void 0, function* () {
                this.error = true;
                this.resetXHRFlags();
                console.error('DESTROY:', error);
                if (!this.toast) {
                    reject(error);
                    return;
                }
                const errorMessage = this.errorMessage(error);
                const formatMessage = errorMessage ? `: ${errorMessage}` : '.';
                Toastify({
                    text: `Unable to Delete ${this.target}${formatMessage}`,
                    duration: 12000,
                    close: true,
                    stopOnFocus: true,
                    style: {
                        background: '#E14D45',
                    }
                }).showToast();
                reject(error);
                return;
            }));
        }));
    }
    errorMessage(error) {
        if (error instanceof ErrorBase) {
            console.error(`[${error.code}] ${error.message}`, error);
            const message = getPreferredStatusMessage(error);
            if (message) {
                return message;
            }
            return error.code !== 'Internal' ? error.message : null;
        }
        const digest = (error.responseText && isJSON(error.responseText)) ? JSON.parse(error.responseText) : null;
        if (!digest) {
            return null;
        }
        const message = getPreferredStatusMessage(digest) || get(digest, 'error.exception[0].message') || null;
        if (!message) {
            return null;
        }
        if (!cookie('env') && has(digest, 'error.exception[0].message')) {
            console.error('[xhr] server:', message);
            return null;
        }
        return message;
    }
}
Stratus.Services.Model = [
    '$provide', ($provide) => {
        $provide.factory('Model', [
            () => {
                return Model;
            }
        ]);
    }
];
Stratus.Data.Model = Model;

//# sourceMappingURL=model.js.map
